import React, { useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

interface PlaceAutocompleteProps {
    handleAddressChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    handlePlaceSelect: (place: google.maps.places.Place) => void;
}

const generateSessionToken = (places: google.maps.PlacesLibrary) => {
    // See https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.sessionToken
    // The session begins when the user starts typing a query, and concludes when they select a place and call Place.fetchFields.
    // 1. Generate a fresh token for each session.
    // 2. Be sure to pass a unique session token for each new session. Using the same token for more than one session will result in each request being billed individually.
    return new places.AutocompleteSessionToken();
}

const PlaceAutocomplete = ({ handleAddressChange, handlePlaceSelect }: PlaceAutocompleteProps) => {
    const [placeSuggestions, setPlaceSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');
    const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!places) { return; }
        if (!sessionToken) { setSessionToken(generateSessionToken(places)); }
        handleAddressChange(e);
        const value = e.target.value;
        if (value && sessionToken) {
            places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: value, sessionToken: sessionToken })
                .then((result: { suggestions: google.maps.places.AutocompleteSuggestion[] }) => {
                    setPlaceSuggestions(result.suggestions.filter(s => s.placePrediction));
                    setShowDropdown(!!result.suggestions && result.suggestions.length > 0);
                })
                .catch(() => {
                    console.error('Error fetching address suggestions from Google Places API');
                    setPlaceSuggestions([]);
                    setShowDropdown(false);
                });
        } else {
            setPlaceSuggestions([]);
            setShowDropdown(false);
        }
    };

    const handleSelect = (selectedSuggestion: google.maps.places.AutocompleteSuggestion) => {
        const prediction = selectedSuggestion.placePrediction;
        const selectedPlace = prediction?.toPlace();

        if (!prediction || !selectedPlace) { throw new Error('No place matched with selected place suggestion'); }

        selectedPlace.fetchFields({
            fields: ['id', 'formattedAddress', 'location']
        }).then((value: { place: google.maps.places.Place }) => {
            if (value.place.location && value.place.formattedAddress) {
                handlePlaceSelect(value.place);
            } else {
                throw new Error('No location or formatted address found');
            }
        });

        if (inputRef.current) {
            inputRef.current.value = prediction.text.text;
            handleAddressChange({ target: { name: 'address', value: prediction.text.text } } as React.ChangeEvent<HTMLInputElement>);
        }

        setSessionToken(null);
        setShowDropdown(false);
    };

    return (
        <div className="relative w-full">
            <input
                ref={inputRef}
                type="text"
                id="address"
                name="address"
                onChange={handleInput}
                onFocus={() => setShowDropdown(placeSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder="Search for an address"
                className="input focus:outline-none w-full"
                autoComplete="off"
            />
            {showDropdown && placeSuggestions.length > 0 && (
                <ul className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-full absolute z-99 top-full left-0">
                    {placeSuggestions.map((suggestion, i) => (
                        <li key={i}>
                            <button type="button" className="w-full text-left" onMouseDown={() => handleSelect(suggestion)}>
                                <span>{suggestion.placePrediction!.text.text}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PlaceAutocomplete;